import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom"; // Import useLocation
// Common
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";

// Auth
import Login from "./components/auth/Login";
import Logout from "./components/auth/Logout";

// Public
import Home from "./components/public/Home"; // Import Home for visitors

// User Panel
import Sidebar from "./components/user/Sidebar";
import "./App.css";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Donor from "./pages/donor/Donor";
import Gifts from "./pages/Gifts";
import GiftDistribution from "./pages/GiftDistribution";
import ManualRecurringDonation from "./pages/ManualRecurringDonation";
import ManualRecurringDonationSummary from "./pages/ManualRecurringDonationSummary";
import Leads from "./pages/Leads";
import Donations from "./pages/Donations";
import RecurringDonation from "./pages/RecurringDonation";
import OneTimeDonationTab from "./pages/OneTimeDonationLeadTab";
import UploadHistory from "./pages/UploadHistory";
import TRNumber from "./pages/TRNumber";
import AccountSettings from "./pages/AccountSettings";
import OtherSetting from "./pages/OtherSetting";
import UserConfirmation from "./pages/UserConfirmation";
import ForgotPassword from "./pages/ForgotPassword";
import EventManagement from "./pages/EventManagement";
import useFetchMasterData from "./hooks/useFetchMasterData";
import Master from "./pages/Master";
import Notification from "./pages/Notification";
import PendingDonation from "./pages/PendingDonation";
import { useAppContext } from "./context/AppContext";
import { cleanupModalBackdrop } from "./utils/modalUtility";

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation(); // Now works correctly inside Router context
  const { state } = useAppContext();
  const userRole = state?.user?.role || localStorage.getItem("user_role");

  useFetchMasterData();

  useEffect(() => {
    const accessTokenCheck = localStorage.getItem("accessToken");
    if (accessTokenCheck) {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    // Hide any lingering bootstrap modal instances on page change
    document.querySelectorAll(".modal.show").forEach((modalEl) => {
      try {
        const instance = window.bootstrap?.Modal?.getInstance(modalEl);
        if (instance) instance.hide();
      } catch (e) {}
    });

    // Clean up any rogue backdrop and restore body scrolling
    cleanupModalBackdrop();

    const timer = setTimeout(() => {
      cleanupModalBackdrop();
    }, 300);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Determine if the current route is "event/:eventUrlSlug"
  const userConfirmationPage =
    location.pathname.startsWith("/user/confirmation");
  const isEventFormPage = location.pathname.startsWith("/event-form/");

  // console.log({userConfirmationPage});

  return (
    <div className="App wrapper">
      {/* Conditionally render Sidebar based on login and role */}

      {isLoggedIn &&
        !userConfirmationPage && // Exclude Sidebar on EventDetail page
        !isEventFormPage && <Sidebar />}

      <div
        className={
          isLoggedIn && !userConfirmationPage && !isEventFormPage
            ? "main-panel"
            : ""
        }
      >
        {/* Conditionally render Header */}
        {isLoggedIn && !userConfirmationPage && !isEventFormPage && <Header />}

        {/* Routes */}
        <Routes>
          {/* Landing page for visitors */}
          <Route
            path="/"
            element={
              isLoggedIn ? (
                userRole === "mentor" ? (
                  <Navigate to={"/donor"} />
                ) : (
                  <Navigate to={"/user/dashboard"} />
                )
              ) : (
                <Home />
              )
            }
          />
          <Route
            path="/login"
            element={<Login setIsLoggedIn={setIsLoggedIn} />}
          />

          <Route path="/logout" element={<Logout />} />

          {/* User routes */}
          <Route
            path="/user/dashboard"
            element={
              isLoggedIn ? (
                userRole === "mentor" ? (
                  <Navigate to="/donor" />
                ) : (
                  <Dashboard />
                )
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/user/users"
            element={
              isLoggedIn ? (
                <Users />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Donor ROutes */}
          <Route
            path="/donor"
            element={
              isLoggedIn ? (
                <Donor />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Gift route */}
          <Route
            path="/user/gifts"
            element={
              isLoggedIn ? (
                <Gifts />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Gift Distrbution route */}
          {/* <Route
            path="/user/gifts/distribution"
            element={
              isLoggedIn ? (
                <GiftDistribution />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          /> */}

          {/* Parent route */}
          <Route
            path="/user/manual-recurring-donation"
            element={
              isLoggedIn ? (
                <ManualRecurringDonation />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          {/* Notification route */}
          <Route
            path="/user/notifications"
            element={
              isLoggedIn ? (
                <Notification />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Manual Recurring  route */}
          <Route
            path="/user/manual-recurring-donation-summary"
            element={
              isLoggedIn ? (
                <ManualRecurringDonationSummary />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Leads  route */}
          {/* <Route
            path="/user/leads"
            element={
              isLoggedIn ? (
                <Leads />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          /> */}

          {/* Add a pending Donation route */}
          <Route
            path="/user/pending-donations"
            element={
              isLoggedIn ? (
                <PendingDonation />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          <Route
            path="/user/master"
            element={
              isLoggedIn ? (
                <Master />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Sbi Donation Route */}
          <Route
            path="/donations"
            element={
              isLoggedIn ? (
                <Donations />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          {/* Add a recurring Donation Route */}
          <Route
            path="/recurring/donations"
            element={
              isLoggedIn ? (
                <RecurringDonation />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a One Time Leads  */}
          <Route
            path="/user/one-time-donation-leads"
            element={
              isLoggedIn ? (
                <OneTimeDonationTab />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a upload historyRoute  */}
          <Route
            path="/upload/history"
            element={
              isLoggedIn ? (
                <UploadHistory />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Tr Number Route  */}
          <Route
            path="/tr-manager"
            element={
              isLoggedIn ? (
                <TRNumber />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          <Route
            path="/user/account-settings"
            element={
              isLoggedIn ? (
                <AccountSettings />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          <Route
            path="/user/other-settings"
            element={
              isLoggedIn ? (
                <OtherSetting />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          <Route
            path="/user/confirmation"
            element={
              isLoggedIn ? (
                <UserConfirmation />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a password forgot PasswordRoute  */}
          <Route
            path="/user/forgot-password"
            element={
              !isLoggedIn ? (
                <ForgotPassword />
              ) : (
                (() => {
                  localStorage.setItem("redirectTo", location.pathname);
                  return <Navigate to="/user/dashboard" />;
                })()
              )
            }
          />

          <Route
            path="/user/events"
            element={
              isLoggedIn ? (
                <EventManagement />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Conditionally render Footer */}
        {isLoggedIn && !userConfirmationPage && !isEventFormPage && <Footer />}
      </div>
    </div>
  );
}

export default App;
